import moment from 'moment';
import { toDateISO, todayISO } from '../../../utils/dateHelpers';
import { CalendarDay } from '../CalendarDay';
import {
  DaysGrid,
  DayWrapper,
  MonthHeader,
  MonthWrapper,
  WeekdayLabel,
  WeekdayRow,
} from './CalendarMonth.styles';
import type { CalendarMonthProps } from './CalendarMonth.types';

function getDefaultWeekdays(): string[] {
  const wd = moment.localeData().weekdaysMin();
  return [...wd.slice(1), wd[0]];
}

function getDefaultMonths(): string[] {
  return moment.localeData().months();
}

function isDateInRange(date: string, start: string, end: string): boolean {
  return date >= start && date <= end;
}

export function CalendarMonth({
  year,
  month,
  availability,
  checkIn,
  checkOut,
  isSelectingCheckOut,
  hoveredDate,
  onDayClick,
  onDayHover,
  labels: l,
}: CalendarMonthProps) {
  const today = todayISO();

  const weekdays = l?.weekdays ?? getDefaultWeekdays();
  const monthNames = l?.months ?? getDefaultMonths();

  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const firstDayJS = new Date(year, month, 1).getDay();
  const startOffset = (firstDayJS + 6) % 7;

  const rangeStart = checkIn || null;
  const rangeEnd = checkOut || (isSelectingCheckOut && hoveredDate ? hoveredDate : null);

  const cells: React.ReactNode[] = [];

  for (let i = 0; i < startOffset; i++) {
    cells.push(
      <DayWrapper key={`empty-${i}`}>
        <CalendarDay />
      </DayWrapper>
    );
  }

  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = toDateISO(year, month, d);
    const dayData = availability?.get(dateStr);
    const isPast = dateStr < today;

    const isCheckIn = dateStr === checkIn;
    const isCheckOut = dateStr === checkOut;
    const isInRange =
      rangeStart && rangeEnd && !isCheckIn && !isCheckOut
        ? isDateInRange(dateStr, rangeStart, rangeEnd)
        : false;

    cells.push(
      <DayWrapper
        key={dateStr}
        onMouseEnter={() => onDayHover?.(dateStr)}
        onMouseLeave={() => onDayHover?.(null)}
      >
        <CalendarDay
          day={d}
          dateString={dateStr}
          status={dayData?.type}
          price={dayData?.price}
          currencyCode={dayData?.currencyCode}
          isCheckIn={isCheckIn}
          isCheckOut={isCheckOut}
          isInRange={isInRange}
          isPast={isPast}
          isSelectingCheckOut={isSelectingCheckOut}
          onClick={onDayClick}
        />
      </DayWrapper>
    );
  }

  return (
    <MonthWrapper>
      <MonthHeader>
        {monthNames[month]} {year}
      </MonthHeader>
      <WeekdayRow>
        {weekdays.map((wd) => (
          <WeekdayLabel key={wd}>{wd}</WeekdayLabel>
        ))}
      </WeekdayRow>
      <DaysGrid>{cells}</DaysGrid>
    </MonthWrapper>
  );
}
